import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { motion } from 'framer-motion';
import { FiCheck, FiAlertTriangle, FiMail } from 'react-icons/fi';
import { activateUser } from '../../api/authAPI';

const ActivationPage = () => {
  const { activation_token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState(
    'Activating your account... Please wait.'
  );

  useEffect(
    () => {
      // Check for activation token in the URL params or query parameters
      const tokenFromParams = activation_token;
      const searchParams = new URLSearchParams(location.search);
      const tokenFromQuery = searchParams.get('activation_token');
      const finalToken = tokenFromParams || tokenFromQuery;

      if (finalToken) {
        const activateEmail = async () => {
          try {
            const res = await activateUser(finalToken);
            setStatus('success');
            setMessage(
              res.message || 'Your account has been successfully activated!'
            );

            // Wait a bit before navigating to login
            setTimeout(() => {
              navigate('/login', { state: { activationSuccess: true } });
            }, 3000);
          } catch (err) {
            console.error('Activation error:', err);
            setStatus('error');
            setMessage(err || 'Your activation token is expired or invalid.');
          }
        };

        activateEmail();
      } else {
        setStatus('error');
        setMessage('No activation token found in the URL.');
      }
    },
    [activation_token, location.search, navigate]
  );

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-green-200 to-blue-300 p-4">
      <motion.div
        className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        {status === 'processing' &&
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
            <h2 className="text-xl font-semibold text-gray-700">
              {message}
            </h2>
          </div>}

        {status === 'success' &&
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-green-100 p-3 rounded-full">
              <FiCheck className="text-green-500 w-8 h-8" />
            </div>
            <h2 className="text-xl font-semibold text-green-600">
              {message}
            </h2>
            <p className="text-gray-600">Redirecting you to login page...</p>
          </div>}

        {status === 'error' &&
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-red-100 p-3 rounded-full">
              <FiAlertTriangle className="text-red-500 w-8 h-8" />
            </div>
            <h2 className="text-xl font-semibold text-red-600">
              Activation Failed
            </h2>
            <p className="text-gray-600">
              {message}
            </p>
            <motion.button
              onClick={() => navigate('/register')}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Registration
            </motion.button>
          </div>}
      </motion.div>
    </div>
  );
};

export default ActivationPage;
